import {
  AnalyzerType,
  ZyusetuFieldKeyFragmentFragment,
  ZyusetuFieldKeyType,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
} from "@/graphql/graphql";
import {useCallback} from "react";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import dayjs, {Dayjs} from "dayjs";
import {parseAnyDate} from "@/plugin/dayjs-plugin";
import {parsePrice} from "@/utils/price-parser";

export function useGetFirstContent() {
  return useCallback(
    (payloads?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment[] | null) => {
      if (!payloads) {
        return;
      }
      
      const geminiResult = payloads.find(
        (payload) => payload.type === AnalyzerType.Gemini,
      );
      if (geminiResult?.content) {
        return geminiResult.content;
      }
      
      const documentAiResult = payloads.find(
        (payload) => payload.type === AnalyzerType.DocumentAi,
      );
      if (documentAiResult?.content) {
        return documentAiResult.content;
      }
      
      const azureResult = payloads.find(
        (payload) => payload.type === AnalyzerType.Azure,
      );
      if (azureResult?.content) {
        return azureResult.content;
      }
    },
    [],
  );
}

export function useGetFirstBoundingRegions() {
  return useCallback(
    (payloads?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment[] | null) => {
      if (!payloads) {
        return;
      }
      
      const documentAiResult = payloads.find(
        (payload) =>
          payload.type === AnalyzerType.DocumentAi && payload.pageAnchor,
      );
      
      if (documentAiResult) {
        return {
          pageAnchor: documentAiResult.pageAnchor,
          confidence: documentAiResult.confidence,
          payload: documentAiResult,
        };
      }
      
      const azureResult = payloads.find(
        (payload) =>
          payload.type === AnalyzerType.Azure && payload.boundingRegions,
      );
      
      if (azureResult) {
        return {
          boundingRegions: azureResult.boundingRegions,
          confidence: azureResult.confidence,
          payload: azureResult,
        };
      }
    },
    [],
  );
}

export function zyusetuValueIntoZodValue(
  key: ZyusetuFieldKeyFragmentFragment,
  value: any,
): FormSchema[string] {
  switch (key.keyType) {
    case ZyusetuFieldKeyType.Boolean:
      return {
        keyType: key.keyType,
        value:
          value === false || value === true
            ? typeof value === "boolean"
              ? value
              : Boolean(value)
            : null,
        payload: {
          availableIf: key.keyStyle.availableIf,
        },
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
      };
    case ZyusetuFieldKeyType.Number:
      // 値段関連は漢数字を数値に変換するパーサーを使う
    {
      const parseNumberValue = (value: any, suffix: string | null | undefined) => {
        if (value === null || value === undefined) return null;
        
        if (suffix === "円") {
          const parsedPrice = parsePrice(value.toString());
          if (parsedPrice !== null) {
            return parsedPrice;
          }
        }
        
        return value instanceof Number ? value.valueOf() : Number(String(value).match(/[0-9０-９,，.．]+/g));
      };
      
      return {
        keyType: key.keyType,
        value: parseNumberValue(value, key.keyStyle.suffix),
        isRequired: !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          thousandSeparator: key.keyStyle.thousandSeparator ?? false,
          availableIf: key.keyStyle.availableIf,
        },
      };
    }
    case ZyusetuFieldKeyType.Select:
      return {
        keyType: key.keyType,
        value: value as string,
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
          items: key.items,
        },
      };
    case ZyusetuFieldKeyType.SelectMany:
      return {
        keyType: key.keyType,
        value:
          value !== null && value !== undefined
            ? Array.isArray(value)
              ? value
              : ([value] as string[])
            : [],
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
          items: key.items,
        },
      };
    // SelectManyWithCustomInput も SelectMany と同じ zod 形式で扱う
    case ZyusetuFieldKeyType.SelectManyWithCustomInput:
      return {
        keyType: key.keyType,
        value:
          value !== null && value !== undefined
            ? Array.isArray(value)
              ? value // 既に string[] の場合
              : typeof value === 'object' && value !== null && 'selected' in value && 'custom' in value
                // value が { selected: string[], custom: string[] } 形式の場合、結合して string[] を返す
                ? [...((value as any).selected ?? []), ...((value as any).custom ?? [])]
                : [] // それ以外の予期しない形式の場合は空配列
            : [], // null や undefined の場合は空配列
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
          items: key.items,
        },
      };
    case ZyusetuFieldKeyType.AutoCompleteBankAccount:
    case ZyusetuFieldKeyType.AutoCompleteManagementCompany:
    case ZyusetuFieldKeyType.StringAutoComplete:
    case ZyusetuFieldKeyType.StringArray:
    case ZyusetuFieldKeyType.String:
      return {
        keyType: key.keyType,
        value: value as string,
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
        },
      };
    case ZyusetuFieldKeyType.DateMonth:
    case ZyusetuFieldKeyType.Date:
      return {
        keyType: key.keyType,
        value:
          value !== null && value !== undefined
            ? value instanceof dayjs
              ? (value as Dayjs)
              : parseAnyDate(value.toString())
            : null,
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
        },
      };
    case ZyusetuFieldKeyType.PremisesLandRights:
      return {
        keyType: key.keyType,
        value: value?.map((right: any) => ({
          index: Number(right.index),
          address: String(right.address),
          chiban: String(right.chiban),
          chimoku: String(right.chimoku),
          chiseki: String(right.chiseki),
          rightType: String(right.rightType),
          rightsRatio: String(right.rightsRatio),
        })),
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
        },
      }
    case ZyusetuFieldKeyType.Tenant:
      return {
        keyType: key.keyType,
        value: value?.map((tenant: any) => ({
          tenant_age: String(tenant.tenant_age),
          tenant_name: tenant.tenant_name,
        })),
        isRequired:
          !key.keyStyle.isHidden && !key.keyStyle.isSpace && key.isRequired,
        payload: {
          availableIf: key.keyStyle.availableIf,
        },
      };
  }
}